import { useCallback } from 'react'
import { useMutation, useReactiveVar } from '@apollo/client'
import { SET_CONSENT } from 'api/consent/set-consent'
import { featureFlags } from 'app/app-feature-flags'
import { CONSENT_POLICY_VERSION } from '../constants'
import { consentVar, isConsentDialogOpenVar } from '../store'
import type { ConsentCategories, ConsentChoice } from '../types'
import { loadBrowserTags } from '../utils/browser-tags'
import { writeConsentCookie } from '../utils/consent-cookie'

const { consentSync } = featureFlags

/** Side effects of a choice on this page load. */
export const applyConsent = (choice: ConsentChoice) => {
  if (choice.analytics) loadBrowserTags()
}

/**
 * Read and change the cookie consent (FE-330).
 *
 * `grant` writes the first-party cookie, updates the reactive var, applies
 * the choice to this page load (loads the browser tags when analytics is on)
 * and, behind the `consentSync` flag, reports it to the backend so the
 * server-side channels respect it too.
 */
export const useConsent = () => {
  const consent = useReactiveVar(consentVar)
  const [setConsent] = useMutation(SET_CONSENT)

  const syncConsent = useCallback(
    (choice: ConsentChoice) => {
      if (!consentSync) return

      setConsent({ variables: { data: choice } }).catch(error =>
        console.error('setConsent failed', error)
      )
    },
    [setConsent]
  )

  const grant = useCallback(
    (categories: ConsentCategories) => {
      const choice: ConsentChoice = {
        ...categories,
        version: CONSENT_POLICY_VERSION,
        grantedAt: new Date().toISOString()
      }

      writeConsentCookie(choice)
      consentVar(choice)
      isConsentDialogOpenVar(false)
      applyConsent(choice)
      syncConsent(choice)
    },
    [syncConsent]
  )

  const acceptAll = useCallback(
    () => grant({ analytics: true, marketing: true }),
    [grant]
  )
  const rejectOptional = useCallback(
    () => grant({ analytics: false, marketing: false }),
    [grant]
  )
  const openDialog = useCallback(() => isConsentDialogOpenVar(true), [])

  return {
    consent,
    isDecided: consent !== null,
    grant,
    acceptAll,
    rejectOptional,
    openDialog,
    syncConsent
  }
}
